import type { CuratedItem as HydratedCuratedItem } from "~types/types";

import { DEFAULT_MEDIA_PRIORITY } from "./constants";
import { type HydratedMediaSourceData, MediaFormat } from "./types";

/**
 * Key function for the MediaDecider, it takes in
 * a CuratedItem and priority array of MediaFormat(s)
 * and uses them to decide which is the winning MediaFormat
 * if no valid data exists, MediaFormat.missingMedia is
 * returned.
 */
export function getWinningMediaFormat(
    source: HydratedCuratedItem,
    priority: MediaFormat[] = DEFAULT_MEDIA_PRIORITY
): MediaFormat {
    const { media } = source;

    // First we determine if we have invalid data for all formats
    // or if the editor has opted for a media-less treatment and
    // act accordingly.
    if (!media) return MediaFormat.missingMedia;
    if (Boolean(media.optOut)) return MediaFormat.optOutMedia;

    // Then we filter priority against available data to
    // determine the winning MediaFormat, which is the first
    // MediaFormat in the MediaPriority array with valid
    // data.
    const [winningMediaFormat] = priority.filter((format) =>
        hasValidMediaDataFor(format, media)
    );

    return winningMediaFormat || MediaFormat.missingMedia;
}

/**
 * Function to confirm a CuratedItem can
 * support at least one MediaFormat in its
 * MediaPriority. Often used in lineup
 * module integrity checks.
 */
export function sourceHasValidMedia(
    source: HydratedCuratedItem,
    priority: MediaFormat[] = DEFAULT_MEDIA_PRIORITY
): boolean {
    return getWinningMediaFormat(source, priority) !== MediaFormat.missingMedia;
}

/**
 * Exported function used to validate whether a CuratedItem's
 * HydratedMediaSourceData has valid data to render a specific
 * MediaFormat. Contains key business logic for GetWinningMediaFormat()
 */
export function hasValidMediaDataFor(
    format: MediaFormat,
    media: HydratedMediaSourceData
): boolean {
    if (format === MediaFormat.audioImage) return Boolean(media.audioImage);
    if (format === MediaFormat.authorImage) return Boolean(media.authorImage);
    if (format === MediaFormat.carousel) return Boolean(media.carousel);
    if (format === MediaFormat.defaultImage) return Boolean(media.defaultImage);
    if (format === MediaFormat.embeddedIframe) {
        return Boolean(media.embeddedIframe);
    }
    if (format === MediaFormat.videoImage) return Boolean(media.videoImage);
    if (format === MediaFormat.vodVideo) return Boolean(media.vodVideo);
    if (format === MediaFormat.magazineCoverImage) {
        return Boolean(media.magazineCoverImage);
    }

    if (format === MediaFormat.verticalVideo) {
        return Boolean(media.verticalVideo);
    }
    if (format === MediaFormat.loopingVideo) return Boolean(media.loopingVideo);
    if (format === MediaFormat.livestreamVideo) {
        return Boolean(media.livestreamVideo);
    }

    if (format === MediaFormat.snapshotChart) {
        return Boolean(media.snapshotChart);
    }

    if (format === MediaFormat.newsletterImage) {
        return Boolean(media.newsletterImage);
    }
    if (format === MediaFormat.optOutMedia) return Boolean(media.optOut);

    return false;
}
